import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    Select,
    SelectItem,
    Chip,
    Card,
    CardBody,
} from "@heroui/react";
import { 
    Lightning, 
    Leaf, 
    ThermometerSimple, 
    Drop, 
    Sun, 
    Wind, 
    House,
    Info,
    CheckCircle,
    XCircle,
    Warning
} from "@phosphor-icons/react";
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';

// EPC Label enum matching backend
export enum EPCLabel {
    A_PLUS = "A+",
    A = "A",
    B = "B", 
    C = "C",
    D = "D",
    E = "E",
    F = "F",
    UNKNOWN = "Onbekend"
}

// Window Advice enum
export enum WindowAdvice {
    HR_GLASS = "Plaatsen hoogrendementsbeglazing",
    WINDOW_PROFILES = "Plaatsen energiezuinige profielen", 
    HR_GLASS_AND_PROFILES = "Plaatsen hoogrendementsbeglazing en energiezuinige profielen",
    UNKNOWN = "Onbekend"
}

// Other enums for comprehensive EPC data
export enum CoolingAndSummerComfort {
    OK = "Geen oververhitting",
    NOT_OK = "Kans op oververhitting", 
    UNKNOWN = "Onbekend"
}

export enum LedLighting {
    OK = "LED-verlichting aanwezig",
    NOT_OK = "Geen LED-verlichting aanwezig",
    UNKNOWN = "Onbekend"
}

export enum SolarBoiler {
    PRESENT = "Zonneboiler aanwezig",
    NOT_PRESENT = "Geen zonneboiler aanwezig",
    UNKNOWN = "Onbekend"
}

export enum SolarPanels {
    PRESENT = "Zonnepanelen aanwezig", 
    NOT_PRESENT = "Geen zonnepanelen aanwezig",
    UNKNOWN = "Onbekend"
}

export enum VentilationSystemType {
    A = "A (Natuurlijke toevoer en afvoer)",
    B = "B (Mechanische toevoer)",
    C = "C (Mechanische afvoer)",
    C_PLUS = "C+ (Mechanische afvoer met vraagsturing)",
    D = "D (Mechanische toevoer en afvoer)",
    NOT_REQUIRED = "Niet verplicht",
    NOT_OK = "Verplicht, maar niet aanwezig",
    UNKNOWN = "Onbekend"
}

interface EPCData {
    id?: number;
    epcLabel: EPCLabel;
    kWh?: string;
    uValueWalls?: number;
    uValueGlazing?: number;
    uValueWindows?: number;
    extraWallInsulationSqm?: number;
    extraRoofInsulationSqm?: number;
    extraFloorInsulationSqm?: number;
    extraWindowAdvice: WindowAdvice;
    coolingAndSummerComfort: CoolingAndSummerComfort;
    coEmission?: string;
    sScore?: string;
    ledLighting: LedLighting;
    solarBoiler: SolarBoiler;
    solarPanels: SolarPanels;
    ventilationSystemType: VentilationSystemType;
}

interface EPCMetadataModalProps {
    isOpen: boolean;
    onClose: () => void;
    fileLogId: number;
    currentEPC?: EPCData | null;
    onSuccess?: () => void;
    unitType?: 'unit' | 'building'; // To determine if it's unit EPC or building EPC
}

const EPCMetadataModal: React.FC<EPCMetadataModalProps> = ({
    isOpen,
    onClose,
    fileLogId,
    currentEPC,
    onSuccess,
    unitType = 'unit'
}) => {
    const { session } = useSession();
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'basic' | 'insulation' | 'systems' | 'sustainability'>('basic');
    
    const [epcData, setEpcData] = useState<EPCData>({
        epcLabel: EPCLabel.UNKNOWN,
        kWh: '',
        uValueWalls: undefined,
        uValueGlazing: undefined,
        uValueWindows: undefined,
        extraWallInsulationSqm: undefined,
        extraRoofInsulationSqm: undefined,
        extraFloorInsulationSqm: undefined,
        extraWindowAdvice: WindowAdvice.UNKNOWN,
        coolingAndSummerComfort: CoolingAndSummerComfort.UNKNOWN,
        coEmission: '',
        sScore: '',
        ledLighting: LedLighting.UNKNOWN,
        solarBoiler: SolarBoiler.UNKNOWN,
        solarPanels: SolarPanels.UNKNOWN,
        ventilationSystemType: VentilationSystemType.UNKNOWN
    });

    // Initialize form with current EPC data
    useEffect(() => {
        if (isOpen && currentEPC) {
            setEpcData(currentEPC);
        } else if (isOpen) {
            // Reset to default values when no current EPC
            setEpcData({
                epcLabel: EPCLabel.UNKNOWN,
                kWh: '',
                uValueWalls: undefined,
                uValueGlazing: undefined,
                uValueWindows: undefined,
                extraWallInsulationSqm: undefined,
                extraRoofInsulationSqm: undefined,
                extraFloorInsulationSqm: undefined,
                extraWindowAdvice: WindowAdvice.UNKNOWN,
                coolingAndSummerComfort: CoolingAndSummerComfort.UNKNOWN,
                coEmission: '',
                sScore: '',
                ledLighting: LedLighting.UNKNOWN,
                solarBoiler: SolarBoiler.UNKNOWN,
                solarPanels: SolarPanels.UNKNOWN,
                ventilationSystemType: VentilationSystemType.UNKNOWN
            });
        }
    }, [isOpen, currentEPC]);

    const getEPCLabelColor = (label: EPCLabel) => {
        switch (label) {
            case EPCLabel.A_PLUS:
            case EPCLabel.A:
                return "bg-status-good text-white";
            case EPCLabel.B:
                return "bg-moss-primary text-white";
            case EPCLabel.C:
                return "bg-yellow-500 text-white";
            case EPCLabel.D:
                return "bg-orange-500 text-white";
            case EPCLabel.E:
                return "bg-brick-primary text-white";
            case EPCLabel.F:
                return "bg-red-700 text-white";
            default:
                return "bg-neutral-secondary-mid text-white";
        }
    };

    const getEPCLabelIcon = (label: EPCLabel) => {
        if ([EPCLabel.A_PLUS, EPCLabel.A, EPCLabel.B].includes(label)) {
            return <CheckCircle size={16} />;
        } else if ([EPCLabel.C, EPCLabel.D].includes(label)) {
            return <Warning size={16} />;
        } else if ([EPCLabel.E, EPCLabel.F].includes(label)) {
            return <XCircle size={16} />;
        }
        return <Info size={16} />;
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            const endpoint = currentEPC?.id 
                ? `${API_URL}/file/update-epc/${currentEPC.id}`
                : `${API_URL}/file/create-epc/${fileLogId}`;
            
            const method = currentEPC?.id ? 'PUT' : 'POST';
            
            await fetchWithAuth(
                endpoint,
                {
                    method,
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(epcData)
                },
                session
            );

            toast.success(currentEPC?.id ? 'EPC gegevens bijgewerkt' : 'EPC gegevens toegevoegd');
            onSuccess?.();
        } catch (error) {
            console.error('Error saving EPC data:', error);
            toast.error('Fout bij het opslaan van EPC gegevens');
        } finally {
            setIsLoading(false);
        }
    };

    const tabs = [
        { key: 'basic', label: 'Basis', icon: <House size={16} /> },
        { key: 'insulation', label: 'Isolatie', icon: <ThermometerSimple size={16} /> },
        { key: 'systems', label: 'Installaties', icon: <Lightning size={16} /> },
        { key: 'sustainability', label: 'Duurzaamheid', icon: <Leaf size={16} /> }
    ];

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose}
            size="4xl"
            scrollBehavior="inside"
            classNames={{
                base: "max-h-[90vh]",
                body: "p-0"
            }}
        >
            <ModalContent>
                <ModalHeader className="flex flex-col gap-2 bg-secondary border-b border-neutral-primary/10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-moss-primary/10 rounded-lg">
                            <Lightning className="text-moss-primary" size={24} />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-neutral-primary">
                                EPC Gegevens {unitType === 'building' ? 'Gemeenschap' : 'Unit'}
                            </h3>
                            <p className="text-sm text-neutral-secondary-mid">
                                Beheer energieprestatiecertificaat metadata
                            </p>
                        </div>
                    </div>
                    
                    {/* EPC Label Preview */}
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-neutral-secondary-mid">Huidig label:</span>
                        <Chip
                            size="lg"
                            variant="solid"
                            className={getEPCLabelColor(epcData.epcLabel)}
                            startContent={getEPCLabelIcon(epcData.epcLabel)}
                        >
                            {epcData.epcLabel}
                        </Chip>
                        {epcData.kWh && (
                            <span className="text-sm text-neutral-secondary-mid">
                                {epcData.kWh} kWh/m²/jaar
                            </span>
                        )}
                    </div>
                </ModalHeader>

                <ModalBody className="p-0">
                    {/* Tab Navigation */}
                    <div className="flex border-b border-neutral-primary/10 bg-neutral-50">
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key as any)}
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                                    activeTab === tab.key
                                        ? 'text-moss-primary border-b-2 border-moss-primary bg-white'
                                        : 'text-neutral-secondary-mid hover:text-neutral-primary'
                                }`}
                            >
                                {tab.icon}
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="p-6">
                        {/* Basic Tab */}
                        {activeTab === 'basic' && (
                            <div className="space-y-6">
                                <Card>
                                    <CardBody className="space-y-4">
                                        <h4 className="font-semibold text-neutral-primary flex items-center gap-2">
                                            <House size={20} />
                                            Basis EPC Gegevens
                                        </h4>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Select
                                                label="EPC Label"
                                                selectedKeys={[epcData.epcLabel]}
                                                onChange={(e) => setEpcData(prev => ({ 
                                                    ...prev, 
                                                    epcLabel: e.target.value as EPCLabel 
                                                }))}
                                                classNames={{
                                                    trigger: "bg-white border border-neutral-primary/20"
                                                }}
                                            >
                                                {Object.values(EPCLabel).map(label => (
                                                    <SelectItem 
                                                        key={label} 
                                                        startContent={
                                                            <div className={`w-4 h-4 rounded-sm ${getEPCLabelColor(label)}`} />
                                                        }
                                                    >
                                                        {label}
                                                    </SelectItem>
                                                ))}
                                            </Select>

                                            <Input
                                                label="Energieverbruik (kWh/m²/jaar)"
                                                value={epcData.kWh || ''}
                                                onChange={(e) => setEpcData(prev => ({ 
                                                    ...prev, 
                                                    kWh: e.target.value 
                                                }))}
                                                placeholder="bijv. 150"
                                                endContent={<span className="text-sm text-neutral-secondary-mid">kWh/m²/jaar</span>}
                                                classNames={{
                                                    inputWrapper: "bg-white border border-neutral-primary/20"
                                                }}
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Input
                                                label="CO₂ Uitstoot"
                                                value={epcData.coEmission || ''}
                                                onChange={(e) => setEpcData(prev => ({ 
                                                    ...prev, 
                                                    coEmission: e.target.value 
                                                }))}
                                                placeholder="bijv. 45"
                                                endContent={<span className="text-sm text-neutral-secondary-mid">kg CO₂/m²/jaar</span>}
                                                classNames={{
                                                    inputWrapper: "bg-white border border-neutral-primary/20"
                                                }}
                                            />

                                            <Input
                                                label="S-Score"
                                                value={epcData.sScore || ''}
                                                onChange={(e) => setEpcData(prev => ({ 
                                                    ...prev, 
                                                    sScore: e.target.value 
                                                }))}
                                                placeholder="bijv. 0.85"
                                                classNames={{
                                                    inputWrapper: "bg-white border border-neutral-primary/20"
                                                }}
                                            />
                                        </div>
                                    </CardBody>
                                </Card>
                            </div>
                        )}

                        {/* Insulation Tab */}
                        {activeTab === 'insulation' && (
                            <div className="space-y-6">
                                <Card>
                                    <CardBody className="space-y-4">
                                        <h4 className="font-semibold text-neutral-primary flex items-center gap-2">
                                            <ThermometerSimple size={20} />
                                            U-Waarden (W/m²K)
                                        </h4>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <Input
                                                label="U-waarde Muren"
                                                type="number"
                                                step="0.01"
                                                value={epcData.uValueWalls?.toString() || ''}
                                                onChange={(e) => setEpcData(prev => ({ 
                                                    ...prev, 
                                                    uValueWalls: e.target.value ? parseFloat(e.target.value) : undefined 
                                                }))}
                                                placeholder="bijv. 0.24"
                                                endContent={<span className="text-sm text-neutral-secondary-mid">W/m²K</span>}
                                                classNames={{
                                                    inputWrapper: "bg-white border border-neutral-primary/20"
                                                }}
                                            />

                                            <Input
                                                label="U-waarde Beglazing"
                                                type="number"
                                                step="0.01"
                                                value={epcData.uValueGlazing?.toString() || ''}
                                                onChange={(e) => setEpcData(prev => ({ 
                                                    ...prev, 
                                                    uValueGlazing: e.target.value ? parseFloat(e.target.value) : undefined 
                                                }))}
                                                placeholder="bijv. 1.1"
                                                endContent={<span className="text-sm text-neutral-secondary-mid">W/m²K</span>}
                                                classNames={{
                                                    inputWrapper: "bg-white border border-neutral-primary/20"
                                                }}
                                            />

                                            <Input
                                                label="U-waarde Ramen"
                                                type="number"
                                                step="0.01"
                                                value={epcData.uValueWindows?.toString() || ''}
                                                onChange={(e) => setEpcData(prev => ({ 
                                                    ...prev, 
                                                    uValueWindows: e.target.value ? parseFloat(e.target.value) : undefined 
                                                }))}
                                                placeholder="bijv. 1.4"
                                                endContent={<span className="text-sm text-neutral-secondary-mid">W/m²K</span>}
                                                classNames={{
                                                    inputWrapper: "bg-white border border-neutral-primary/20"
                                                }}
                                            />
                                        </div>
                                    </CardBody>
                                </Card>

                                <Card>
                                    <CardBody className="space-y-4">
                                        <h4 className="font-semibold text-neutral-primary">
                                            Extra Isolatie Aanbevelingen (m²)
                                        </h4>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <Input
                                                label="Extra Muurisolatie"
                                                type="number"
                                                step="0.1"
                                                value={epcData.extraWallInsulationSqm?.toString() || ''}
                                                onChange={(e) => setEpcData(prev => ({ 
                                                    ...prev, 
                                                    extraWallInsulationSqm: e.target.value ? parseFloat(e.target.value) : undefined 
                                                }))}
                                                placeholder="bijv. 25.5"
                                                endContent={<span className="text-sm text-neutral-secondary-mid">m²</span>}
                                                classNames={{
                                                    inputWrapper: "bg-white border border-neutral-primary/20"
                                                }}
                                            />

                                            <Input
                                                label="Extra Dakisolatie"
                                                type="number"
                                                step="0.1"
                                                value={epcData.extraRoofInsulationSqm?.toString() || ''}
                                                onChange={(e) => setEpcData(prev => ({ 
                                                    ...prev, 
                                                    extraRoofInsulationSqm: e.target.value ? parseFloat(e.target.value) : undefined 
                                                }))}
                                                placeholder="bijv. 15.0"
                                                endContent={<span className="text-sm text-neutral-secondary-mid">m²</span>}
                                                classNames={{
                                                    inputWrapper: "bg-white border border-neutral-primary/20"
                                                }}
                                            />

                                            <Input
                                                label="Extra Vloerisolatie"
                                                type="number"
                                                step="0.1"
                                                value={epcData.extraFloorInsulationSqm?.toString() || ''}
                                                onChange={(e) => setEpcData(prev => ({ 
                                                    ...prev, 
                                                    extraFloorInsulationSqm: e.target.value ? parseFloat(e.target.value) : undefined 
                                                }))}
                                                placeholder="bijv. 20.0"
                                                endContent={<span className="text-sm text-neutral-secondary-mid">m²</span>}
                                                classNames={{
                                                    inputWrapper: "bg-white border border-neutral-primary/20"
                                                }}
                                            />
                                        </div>

                                        <Select
                                            label="Raam Aanbeveling"
                                            selectedKeys={[epcData.extraWindowAdvice]}
                                            onChange={(e) => setEpcData(prev => ({ 
                                                ...prev, 
                                                extraWindowAdvice: e.target.value as WindowAdvice 
                                            }))}
                                            classNames={{
                                                trigger: "bg-white border border-neutral-primary/20"
                                            }}
                                        >
                                            {Object.values(WindowAdvice).map(advice => (
                                                <SelectItem key={advice}>
                                                    {advice}
                                                </SelectItem>
                                            ))}
                                        </Select>
                                    </CardBody>
                                </Card>
                            </div>
                        )}

                        {/* Systems Tab */}
                        {activeTab === 'systems' && (
                            <div className="space-y-6">
                                <Card>
                                    <CardBody className="space-y-4">
                                        <h4 className="font-semibold text-neutral-primary flex items-center gap-2">
                                            <Lightning size={20} />
                                            Installaties & Systemen
                                        </h4>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Select
                                                label="Ventilatie Systeem"
                                                selectedKeys={[epcData.ventilationSystemType]}
                                                onChange={(e) => setEpcData(prev => ({ 
                                                    ...prev, 
                                                    ventilationSystemType: e.target.value as VentilationSystemType 
                                                }))}
                                                classNames={{
                                                    trigger: "bg-white border border-neutral-primary/20"
                                                }}
                                                startContent={<Wind size={16} />}
                                            >
                                                {Object.values(VentilationSystemType).map(type => (
                                                    <SelectItem key={type}>
                                                        {type}
                                                    </SelectItem>
                                                ))}
                                            </Select>

                                            <Select
                                                label="LED Verlichting"
                                                selectedKeys={[epcData.ledLighting]}
                                                onChange={(e) => setEpcData(prev => ({ 
                                                    ...prev, 
                                                    ledLighting: e.target.value as LedLighting 
                                                }))}
                                                classNames={{
                                                    trigger: "bg-white border border-neutral-primary/20"
                                                }}
                                                startContent={<Lightning size={16} />}
                                            >
                                                {Object.values(LedLighting).map(lighting => (
                                                    <SelectItem key={lighting}>
                                                        {lighting}
                                                    </SelectItem>
                                                ))}
                                            </Select>
                                        </div>

                                        <Select
                                            label="Koeling & Zomercomfort"
                                            selectedKeys={[epcData.coolingAndSummerComfort]}
                                            onChange={(e) => setEpcData(prev => ({ 
                                                ...prev, 
                                                coolingAndSummerComfort: e.target.value as CoolingAndSummerComfort 
                                            }))}
                                            classNames={{
                                                trigger: "bg-white border border-neutral-primary/20"
                                            }}
                                            startContent={<ThermometerSimple size={16} />}
                                        >
                                            {Object.values(CoolingAndSummerComfort).map(comfort => (
                                                <SelectItem key={comfort}>
                                                    {comfort}
                                                </SelectItem>
                                            ))}
                                        </Select>
                                    </CardBody>
                                </Card>
                            </div>
                        )}

                        {/* Sustainability Tab */}
                        {activeTab === 'sustainability' && (
                            <div className="space-y-6">
                                <Card>
                                    <CardBody className="space-y-4">
                                        <h4 className="font-semibold text-neutral-primary flex items-center gap-2">
                                            <Leaf size={20} />
                                            Duurzame Energie
                                        </h4>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Select
                                                label="Zonnepanelen"
                                                selectedKeys={[epcData.solarPanels]}
                                                onChange={(e) => setEpcData(prev => ({ 
                                                    ...prev, 
                                                    solarPanels: e.target.value as SolarPanels 
                                                }))}
                                                classNames={{
                                                    trigger: "bg-white border border-neutral-primary/20"
                                                }}
                                                startContent={<Sun size={16} />}
                                            >
                                                {Object.values(SolarPanels).map(panels => (
                                                    <SelectItem key={panels}>
                                                        {panels}
                                                    </SelectItem>
                                                ))}
                                            </Select>

                                            <Select
                                                label="Zonneboiler"
                                                selectedKeys={[epcData.solarBoiler]}
                                                onChange={(e) => setEpcData(prev => ({ 
                                                    ...prev, 
                                                    solarBoiler: e.target.value as SolarBoiler 
                                                }))}
                                                classNames={{
                                                    trigger: "bg-white border border-neutral-primary/20"
                                                }}
                                                startContent={<Drop size={16} />}
                                            >
                                                {Object.values(SolarBoiler).map(boiler => (
                                                    <SelectItem key={boiler}>
                                                        {boiler}
                                                    </SelectItem>
                                                ))}
                                            </Select>
                                        </div>
                                    </CardBody>
                                </Card>

                                {/* EPC Summary Card */}
                                <Card className="bg-moss-primary/5 border border-moss-primary/20">
                                    <CardBody>
                                        <h4 className="font-semibold text-moss-primary mb-3">
                                            EPC Samenvatting
                                        </h4>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                            <div>
                                                <span className="text-neutral-secondary-mid">Label:</span>
                                                <div className="font-medium">{epcData.epcLabel}</div>
                                            </div>
                                            <div>
                                                <span className="text-neutral-secondary-mid">Verbruik:</span>
                                                <div className="font-medium">{epcData.kWh || 'Niet ingevuld'} kWh/m²/jaar</div>
                                            </div>
                                            <div>
                                                <span className="text-neutral-secondary-mid">CO₂:</span>
                                                <div className="font-medium">{epcData.coEmission || 'Niet ingevuld'} kg/m²/jaar</div>
                                            </div>
                                            <div>
                                                <span className="text-neutral-secondary-mid">S-Score:</span>
                                                <div className="font-medium">{epcData.sScore || 'Niet ingevuld'}</div>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </div>
                        )}
                    </div>
                </ModalBody>

                <ModalFooter className="border-t border-neutral-primary/10 bg-neutral-50">
                    <div className="flex justify-between w-full">
                        <div className="flex gap-2">
                            <Button
                                variant="light"
                                onPress={onClose}
                                isDisabled={isLoading}
                            >
                                Annuleren
                            </Button>
                            <Button
                                className="bg-moss-primary text-white"
                                onPress={handleSave}
                                isLoading={isLoading}
                            >
                                {currentEPC?.id ? 'Bijwerken' : 'Opslaan'}
                            </Button>
                        </div>
                    </div>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default EPCMetadataModal; 